import React, { useState, useEffect } from "react";
import {
  ChakraProvider,
  Box,
  Button,
  Card,
  CardBody,
  Center,
  Image,
  Flex,
  Text,
  HStack,
  Heading,
  VStack,
  Grid,
  FormLabel,
  GridItem,
  useColorModeValue,
  Input,
  InputGroup,
  InputLeftAddon,
  Divider,
} from "@chakra-ui/react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ColorModeSwitcher } from "../../ColorModeSwitcher";
import { Nav } from "../../containers/Nav";
import { Footer } from "../../containers/Footer";
import { getBrandCampaign, editBrandCampaign } from "../../actions/campaigns";
import CampaignCard from "../../components/campaignCard";
import AdminNav from "../../components/adminNav";
import { validateAdminAuth, truncateLongText } from "../../helpers";

function AdminEditCampaign() {
  const navigate = useNavigate();

  const params = useParams();
  const brand = params.brand;
  const campaign = params.campaign;

  const [brandCampaign, setBrandCampaign] = useState({});
  const [fetchingCampaigns, setFetchingCampaigns] = useState(false);

  const [noCampaigns, setNoCampaigns] = useState(false);
  const lightGray = useColorModeValue("gray.100", "gray.900");

  const [newBuckets, setNewBuckets] = useState([]);

  const [campaignName, setCampaignName] = useState("");
  const [newCampaignName, setNewCampaignName] = useState("");
  const [campaignCover, setCampaignCover] = useState("");
  const [newCampaignCover, setNewCampaignCover] = useState("");
  const [campaignDescription, setCampaignDescription] = useState("");
  const [campaignMedium, setCampaignMedium] = useState("");
  const [campaignGenre, setCampaignGenre] = useState("");
  const [coverImageErr, setCoverImageErr] = useState("");

  const [newBucketText, setNewBucketText] = useState("");
  const [changesSaved, setChangesSaved] = useState(false);
  //const [newBucketImage, setNewBucketImage] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveError, setSaveError] = useState("");

  const archiveCampaign = async () => {
    try { 
      setIsSubmitting(true);
      const updateObj = {
        id: brandCampaign._id,
        archived: true,
        name: brandCampaign.name,
        cover: brandCampaign.cover,
        description: brandCampaign.description,
        medium: brandCampaign.medium,
        genre: brandCampaign.genre,
      };
      updateObj.buckets = brandCampaign.buckets;
      await editBrandCampaign(params.brand, updateObj);
      setChangesSaved(true);
    } catch (err) {
      console.log(err);
      setChangesSaved(false);
      setSaveError("There was an issue with this request");
    } finally {
      setIsSubmitting(false);
    }
  }

  const submitCampaignChanges = async () => {
    return new Promise(async (resolve, reject) => {
      if (isSubmitting) resolve();
      setIsSubmitting(true);
      const updateObj = {
        id: brandCampaign._id,
        name: newCampaignName || brandCampaign.name,
        cover: newCampaignCover || brandCampaign.cover,
        description: campaignDescription || brandCampaign.description,
        medium: campaignMedium || brandCampaign.medium,
        genre: campaignGenre || brandCampaign.genre,
      };

      if (newBuckets.length) {
        updateObj.buckets = newBuckets;
      } else {
        updateObj.buckets = brandCampaign.buckets;
      }

      try {
        await editBrandCampaign(params.brand, updateObj);
        setChangesSaved(true);
      } catch (err) {
        console.log(err);
        setChangesSaved(false);
        setSaveError("There was an issue with this request");
      } finally {
        setIsSubmitting(false);
      }

      resolve();
    });
  };

  const submitCoverImage = (image) => {
    setCoverImageErr("");
    if (!image) return;
    if (image.size / 1000 / 1000 > 25) {
      setCoverImageErr("Image can't be larger than 25mb");
    }
    console.log(image)
    setNewCampaignCover(image);
  };

  const userInfo = validateAdminAuth();
  if (userInfo?.organization) {
    navigate("/brandCampaign");
  } else if (!userInfo) {
    navigate("/login");
  }

  useEffect(() => {
    setFetchingCampaigns(true);

    getBrandCampaign(brand, campaign).then((editCampaign) => {
      if (editCampaign.length) {
        setNoCampaigns(true);
      }
      setNewBuckets(editCampaign.buckets);
      setBrandCampaign(editCampaign);
      setCampaignName(editCampaign.name);
      setCampaignCover(editCampaign.cover);
      setCampaignDescription(editCampaign.description);
      setCampaignMedium(editCampaign.medium);
      setCampaignGenre(editCampaign.genre);
      setCampaignCover(editCampaign.cover);

      setFetchingCampaigns(false);
    });
  }, []);

  return (
    <Grid
      templateAreas={`"header header" "main main" "footer footer"`}
      gridTemplateRows={"50px 1fr 30px"}
      gridTemplateColumns={"150px 1fr"}
      color="blackAlpha.700"
      pb="75px"
    >
      <Nav />

      <GridItem pl="2" area={"main"}>
        <Center>
          <VStack>
            <AdminNav />

            {!fetchingCampaigns && brandCampaign.length !== 0 && (
              <Card
                minWidth="650px"
                maxWidth="1200px"
                mx="5"
                my="5"
                p="3"
                bg={lightGray}
              >
                <Flex my="4">
                  <Box width="100%">
                    <Heading fontSize="2xl">Edit Campaign</Heading>
                  </Box>

                  <VStack>
                    <HStack>
                      <Box>
                        <Button
                          onClick={() => archiveCampaign()}
                          colorScheme="orange"
                          fontSize="sm"
                          isDisabled={brandCampaign.archived}
                          isLoading={isSubmitting}
                        >
                          { brandCampaign.archived ? "Archived" : "Archive" }
                        </Button>
                      </Box>
                      <Box>
                        <Button
                          onClick={() => submitCampaignChanges()}
                          colorScheme="green"
                          fontSize="sm"
                          isLoading={isSubmitting}
                        >
                          Save changes
                        </Button>
                      </Box>
                    </HStack>

                    <Box>
                      <Text fontSize="xs" color="red.500">
                        {saveError}
                      </Text>
                    </Box>
                  </VStack>
                </Flex>

                <Box mb="3">
                  <InputGroup>
                    <InputLeftAddon children="name" />
                    <Input
                      disabled="true"
                      type="text"
                      onChange={(e) => setNewCampaignName(e.target.value)}
                      value={campaignName}
                    />
                  </InputGroup>
                </Box>
                <Box mb="3">
                  <InputGroup>
                    <InputLeftAddon children="Production Company" />
                    <Input
                      type="text"
                      value={brandCampaign.brand}
                      isDisabled="true"
                    />
                  </InputGroup>
                </Box>
                <Box mb="3">
                  <InputGroup>
                    <InputLeftAddon children="Cover" />
                    <VStack>
                      <Image
                        src={
                          newCampaignCover
                            ? URL.createObjectURL(newCampaignCover)
                            : "https://hubitprototype.herokuapp.com" +
                              campaignCover
                        }
                        objectFit="cover"
                        boxSize="180px"
                      />

                      <Input
                        display="none"
                        position="absolute"
                        width="0px"
                        name="cover-image-upload"
                        id="cover-image-upload"
                        accept="image/png, image/jpeg"
                        type="file"
                        onChange={(e) => {
                          submitCoverImage(e.target.files[0]);
                        }}
                      ></Input>
                      <Text as="span" color="red.500">
                        {coverImageErr}
                      </Text>
                      <FormLabel
                        borderRadius="md"
                        border="1px"
                        cursor="pointer"
                        padding="6px"
                        outline="0"
                        htmlFor="cover-image-upload"
                      >
                        <Text as="b">+ Upload new cover image</Text>
                      </FormLabel>
                    </VStack>
                  </InputGroup>
                </Box>
                <Box mb="3">
                  <InputGroup>
                    <InputLeftAddon children="Description" />
                    <Input
                      type="text"
                      onChange={(e) => setCampaignDescription(e.target.value)}
                      value={campaignDescription}
                    />
                  </InputGroup>
                </Box>
                <Box mb="3">
                  <InputGroup>
                    <InputLeftAddon children="Genre" />
                    <Input
                      type="text"
                      onChange={(e) => setCampaignGenre(e.target.value)}
                      value={campaignGenre}
                    />
                  </InputGroup>
                </Box>
                <Box mb="3">
                  <InputGroup>
                    <InputLeftAddon children="Medium" />
                    <Input
                      type="text"
                      onChange={(e) => setCampaignMedium(e.target.value)}
                      value={campaignMedium}
                    />
                  </InputGroup>
                </Box>

                <Divider />
                <Flex m="2">
                  <Box width="100%">
                    <Heading fontSize="2xl">Edit Campaign Buckets</Heading>
                  </Box>
                </Flex>

                <EditCampaignBuckets
                  newBuckets={newBuckets}
                  setBrandCampaign={setBrandCampaign}
                  setNewBuckets={setNewBuckets}
                  brandCampaign={brandCampaign}
                  setNewBucketText={setNewBucketText}
                />
              </Card>
            )}
          </VStack>
        </Center>
      </GridItem>

      <Footer />
    </Grid>
  );
}

const EditCampaignBuckets = ({
  newBuckets,
  setBrandCampaign,
  setNewBuckets,
  brandCampaign,
  setNewBucketText,
  coverImageErr,
}) => {
  const [renderBuckets, setRenderBuckets] = useState(newBuckets || []);
  if(newBuckets.length){
    delete newBuckets[0]["_id"]
    delete newBuckets[0]["status"]
    console.log({newBuckets})
  }

  const removeBucketKey = (bucket, key) => {
    let newBucket = [...bucket];
    newBuckets.map((b) => {
      if (b[key]) {
        delete newBuckets[0][key];
      }
      return b;
    });
    setBrandCampaign({ ...brandCampaign, buckets: newBuckets });
    setNewBuckets(newBuckets);
  };

  const removeBucketValue = (bucket, key, i) => {
    let newBucket = [...bucket];
    newBucket.splice(i, 1);
    newBuckets.map((b) => {
      if (b[key]) {
        b[key] = [...newBucket];
      }
      return b;
    });
    setBrandCampaign({ ...brandCampaign, buckets: newBuckets });
    setNewBuckets(newBuckets);
  };

  const addBucketValue = (bucket, key, value) => {
    let newBucket = [...bucket];
    newBucket.push(value);

    newBuckets.map((b) => {
      if (b[key]) {
        b[key] = [...newBucket];
      }
      return b;
    });

    setBrandCampaign({ ...brandCampaign, buckets: newBuckets });
    setNewBuckets(newBuckets);
    setNewBucketText("");
  };

  //useEffect(() => {
  //  setRenderBuckets(newBuckets);
  //}, [newBuckets]);

  const submitBucketImage = (bucket, key, image) => {
    if (!image) return;

    let newBucket = [...bucket];
    newBucket.push(image);

    newBuckets.map((b) => {
      if (b[key]) {
        b[key] = [...newBucket];
      }
      return b;
    });

    setBrandCampaign({ ...brandCampaign, buckets: newBuckets });
    setNewBuckets(newBuckets);
    setNewBucketText("");
    //setNewCampaignCover(image);
  };

  return renderBuckets?.map((bucket) => {
    return Object.keys(bucket)?.map((key) => {
      return (
        <>
          {["_id", "status"].includes(key) ? (
            <></>
          ) : (
            <HStack mb="50px" spacing="0" align="top">
              <>
                <Button
                  onClick={() => {
                    removeBucketKey(bucket[key], key);
                  }}
                  colorScheme="red"
                  mx="3"
                >
                  x
                </Button>
                <Box position="relative">
                  <Text as="b" mr="7">{key}</Text>
                </Box>

                <VStack width="100%">
                  {Array.isArray(bucket[key]) ? (
                    bucket[key]?.map((bucketValue, idx) => {
                      return (
                        <HStack spacing="0" width="100%">
                          <Button
                            onClick={() =>
                              removeBucketValue(bucket[key], key, idx)
                            }
                            colorScheme="red"
                            ml="4"
                          >
                            x
                          </Button>
                          {bucketValue.name ||
                          bucketValue.includes("/api/file/") ? (
                            <Image
                              src={
                                bucketValue.name
                                  ? URL.createObjectURL(bucketValue)
                                  : "https://hubitprototype.herokuapp.com" +
                                    bucketValue
                              }
                              objectFit="cover"
                              boxSize="80px"
                            />
                          ) : (
                            <Input type="text" value={bucketValue} />
                          )}
                        </HStack>
                      );
                    })
                  ) : (
                    <Input type="text" value={bucket[key]} />
                  )}
                  {(bucket[key].length && bucket[key][0].includes("file")) ||
                  key.includes("Links") ? (
                    <>
                      <Input
                        display="none"
                        position="absolute"
                        width="0px"
                        id={`${key}-image-upload`}
                        name={`${key}-image-upload`}
                        accept="image/png, image/jpeg"
                        type="file"
                        onChange={(e) => {
                          submitBucketImage(
                            bucket[key],
                            key,
                            e.target.files[0]
                          );
                        }}
                      ></Input>
                      <Text as="span" color="red.500">
                        {coverImageErr}
                      </Text>
                      <FormLabel
                        borderRadius="md"
                        border="1px"
                        cursor="pointer"
                        padding="6px"
                        outline="0"
                        htmlFor={`${key}-image-upload`}
                      >
                        <Text as="b">+ Upload new image</Text>
                      </FormLabel>
                    </>
                  ) : (
                    <>
                      <HStack spacing="0" width="100%">
                        <Button
                          onClick={() => {
                            addBucketValue(
                              bucket[key],
                              key,
                              document.getElementById(key + "-input").value
                            );
                            document.getElementById(key + "-input").value = "";
                          }}
                          colorScheme="green"
                          ml="4"
                        >
                          +
                        </Button>
                        <Input type="text" id={`${key}-input`} />
                      </HStack>
                    </>
                  )}
                </VStack>
              </>
            </HStack>
          )}
        </>
      );
    });
  });
};

export default AdminEditCampaign;
